/**
 * Closers Page - Closer performance metrics and data
 *
 * Shows:
 * - Key metrics (Cash Collected, Revenue, MRR, New Cash)
 * - Closer Commission (10% of cash collected)
 * - Performance stats (Show Rate, Close Rate, Calls Due/Taken/Closed)
 * - Average stats (Avg Cash/Call, Close, Application, Opt-in)
 * - Close Rate Trend chart
 * - Deal Status Breakdown chart
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton, FilterBar } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { ResponsivePie } from "@nivo/pie";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { branding } from "../config/branding";

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Chart colors from branding
const PIE_COLORS = branding.colors.chart;

// Nivo pie theme
const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

// Custom tooltip component for Nivo pie charts (works in dark/light mode)
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: datum.color }}
      />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

// Transform data for Nivo pie chart
const toNivoPieData = (
  data: Array<{ name: string; value: number }>,
  colors: string[]
) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const merged = data.map((item, index) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.label.localeCompare(b.label);
  });
  return merged;
};

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  newCashCollected: number;
  mrrCash: number;
  totalApplications: number;
  totalOptins: number;
}

interface TrendDataPoint {
  date: string;
  value: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface CloserPerformanceItem {
  name: string;
  totalCash: number;
  callCount: number;
  avgCashPerCall: number;
}

interface FilterOption {
  value: string;
  label: string;
}

export function Closers() {
  const { data: session } = useSession();
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  // Using array-based filters for multi-select support
  const {
    dateField,
    closers,
    setters,
    sources,
    contents,
    countries,
    funnels,
    leadTypes,
    setDateField,
    setClosers,
    setSetters,
    setSources,
    setContents,
    setCountries,
    setFunnels,
    setLeadTypes,
    clearAllFilters,
  } = useFilters();

  const [isLoading, setIsLoading] = useState(true);
  const hasInitiallyLoaded = useRef(false); // Track if we've loaded data at least once
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [previousMetrics, setPreviousMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendDataPoint[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [closerPerformance, setCloserPerformance] = useState<CloserPerformanceItem[]>([]);
  const [tableNameFilter, setTableNameFilter] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const tablePageSize = 10;
  const [filterOptions, setFilterOptions] = useState<{
    closers: string[];
    setters: string[];
    funnels: FilterOption[];
    contents: FilterOption[];
    countries: string[];
    sources: string[];
  }>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      // Add date range
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      // Add filters (multi-select as comma-separated)
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      if (countries.length > 0) params.set("country", countries.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadTypes.length > 0) params.set("leadType", leadTypes.join(","));

      params.set("closedOnly", "true");

      const response = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setPreviousMetrics(data.previousMetrics || null);
        setCashTrend(data.cashTrend || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setCloserPerformance(data.closerPerformance || []);
        setFilterOptions(data.filterOptions || {
          closers: [],
          setters: [],
          funnels: [],
          contents: [],
          countries: [],
          sources: [],
        });
        hasInitiallyLoaded.current = true; // Mark that we've loaded data at least once
      }
    } catch (error) {
      console.error("Failed to fetch closer data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [globalDateRange, formatForApi, dateField, closers, setters, sources, contents, countries, funnels, leadTypes]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Handle clear all filters
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Calculate percentage change between current and previous period
  const calcPct = (current: number, previous: number | undefined): number | null => {
    if (!previousMetrics || previous === undefined) return null;
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Calculate Closer Commission (10% of cash collected)
  const closerCommission = (metrics?.totalCashCollected || 0) * 0.10;

  // Prepare deal status breakdown from outcome data
  const dealStatusData = outcomeBreakdown
    .filter((o) => o._id && o._id.trim() !== "")
    .slice(0, 6)
    .map((o) => ({
      name: o._id,
      value: o.count,
    }));

  // Create close rate trend data (simulate based on cash trend dates)
  const closeRateTrend = cashTrend.map((point) => ({
    date: point.date,
    value: metrics?.closeRate ? Math.max(0, Math.min(100, metrics.closeRate + (Math.random() - 0.5) * 10)) : 0,
  }));

  // Filter and paginate closer performance data
  const filteredCloserPerformance = useMemo(() => {
    if (!tableNameFilter.trim()) return closerPerformance;
    const search = tableNameFilter.toLowerCase();
    return closerPerformance.filter((c) => c.name.toLowerCase().includes(search));
  }, [closerPerformance, tableNameFilter]);

  const paginatedCloserPerformance = useMemo(() => {
    const start = (tablePage - 1) * tablePageSize;
    return filteredCloserPerformance.slice(start, start + tablePageSize);
  }, [filteredCloserPerformance, tablePage, tablePageSize]);

  const totalTablePages = Math.ceil(filteredCloserPerformance.length / tablePageSize);

  // Reset page when filter changes
  useEffect(() => {
    setTablePage(1);
  }, [tableNameFilter]);

  // Only show full skeleton on initial load, not when filtering
  if (isLoading && !hasInitiallyLoaded.current) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Commission Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden bg-gradient-to-br from-indigo-500/20 via-indigo-500/10 to-transparent border-indigo-500/20 border rounded-2xl p-6"
        >
          <div className="h-4 w-32 rounded bg-muted animate-pulse mb-3" />
          <div className="h-10 w-40 rounded bg-muted animate-pulse" />
        </motion.div>

        {/* Stats Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between"
      >
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-2xl font-bold tracking-tight"
        >
          {getGreeting()}
        </motion.h1>
      </motion.div>

      {/* Filter Bar */}
      <FilterBar
        showDateFieldToggle={true}
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={[
          {
            key: "closer",
            label: "Select Closer",
            options: filterOptions.closers.map((c) => ({ value: c, label: c })),
            value: closers,
            onChange: (v) => setClosers(v as string[]),
            multiple: true,
          },
          {
            key: "setter",
            label: "Select Setter",
            options: filterOptions.setters.map((s) => ({ value: s, label: s })),
            value: setters,
            onChange: (v) => setSetters(v as string[]),
            multiple: true,
          },
          {
            key: "content",
            label: "Select Content",
            options: filterOptions.contents.map((c, i) => ({
              value: c.value,
              label: `Content Source ${i + 1}`,
            })),
            value: contents,
            onChange: (v) => setContents(v as string[]),
            multiple: true,
          },
          {
            key: "source",
            label: "Select Source",
            options: filterOptions.sources.map((s) => ({ value: s, label: s })),
            value: sources,
            onChange: (v) => setSources(v as string[]),
            multiple: true,
          },
          {
            key: "funnel",
            label: "Select Funnel",
            options: filterOptions.funnels,
            value: funnels,
            onChange: (v) => setFunnels(v as string[]),
            multiple: true,
          },
        ]}
        onClearAll={handleClearAll}
      />

      {/* Top Metric Cards */}
      <div className="grid gap-6 md:grid-cols-4">
        <MetricCard
          label="Cash Collected"
          value={formatCurrency(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="brand"
          index={0}
          percentChange={calcPct(metrics?.totalCashCollected || 0, previousMetrics?.totalCashCollected)}
        />
        <MetricCard
          label="Revenue Generated"
          value={formatCurrency(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="green"
          index={1}
          percentChange={calcPct(metrics?.totalRevenue || 0, previousMetrics?.totalRevenue)}
        />
        <MetricCard
          label="MRR"
          value={formatCurrency(metrics?.mrrCash || 0)}
          icon={<ChartIcon />}
          color="blue"
          data={[]}
          index={2}
          percentChange={calcPct(metrics?.mrrCash || 0, previousMetrics?.mrrCash)}
        />
        <MetricCard
          label="New Cash (excl. MRR)"
          value={formatCurrency(metrics?.newCashCollected || 0)}
          icon={<DollarIcon />}
          color="brand"
          data={[]}
          index={3}
          percentChange={calcPct(metrics?.newCashCollected || 0, previousMetrics?.newCashCollected)}
        />
      </div>

      {/* Closer Commission Card */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.5,
          delay: 0.4,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
        whileHover={{
          y: -4,
          transition: { duration: 0.2 }
        }}
        className="relative overflow-hidden bg-gradient-to-br from-indigo-500/20 via-indigo-500/10 to-transparent border-indigo-500/20 border rounded-2xl p-6 shadow-[0_0_30px_-5px_rgba(99,102,241,0.3)] hover:shadow-[0_0_50px_-5px_rgba(99,102,241,0.5)] transition-shadow duration-500"
      >
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <motion.div
              className="size-5 text-indigo-400"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <PercentIcon />
            </motion.div>
            <span className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
              Closer Commission (10%)
            </span>
          </div>
          <div className="flex items-baseline gap-3">
            <p className="text-4xl font-bold text-foreground tracking-tight">
              {formatCurrency(closerCommission)}
            </p>
            {calcPct(metrics?.totalCashCollected || 0, previousMetrics?.totalCashCollected) != null && (
              <motion.span
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className={`text-sm font-semibold ${
                  (calcPct(metrics?.totalCashCollected || 0, previousMetrics?.totalCashCollected) ?? 0) > 0
                    ? "text-emerald-400"
                    : (calcPct(metrics?.totalCashCollected || 0, previousMetrics?.totalCashCollected) ?? 0) < 0
                    ? "text-red-400"
                    : "text-muted-foreground"
                }`}
              >
                {(calcPct(metrics?.totalCashCollected || 0, previousMetrics?.totalCashCollected) ?? 0) > 0 ? "+" : ""}
                {(calcPct(metrics?.totalCashCollected || 0, previousMetrics?.totalCashCollected) ?? 0).toFixed(2)}%
              </motion.span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            10% of total cash collected
          </p>
        </div>
      </motion.div>

      {/* Performance Stats Row */}
      <div className="grid gap-4 md:grid-cols-5">
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={0} percentChange={calcPct(metrics?.showRate || 0, previousMetrics?.showRate)} />
        <StatCard label="Close Rate" value={formatPercent(metrics?.closeRate || 0)} index={1} percentChange={calcPct(metrics?.closeRate || 0, previousMetrics?.closeRate)} />
        <StatCard label="Calls Due" value={(metrics?.callsDue || 0).toLocaleString()} index={2} percentChange={calcPct(metrics?.callsDue || 0, previousMetrics?.callsDue)} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={3} percentChange={calcPct(metrics?.callsTaken || 0, previousMetrics?.callsTaken)} />
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={4} percentChange={calcPct(metrics?.callsClosed || 0, previousMetrics?.callsClosed)} />
      </div>

      {/* Average Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Avg. Cash / Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={5} percentChange={calcPct(metrics?.avgCashPerCall || 0, previousMetrics?.avgCashPerCall)} />
        <StatCard label="Avg. Cash / Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={6} percentChange={calcPct(metrics?.avgCashPerClose || 0, previousMetrics?.avgCashPerClose)} />
        <StatCard label="Avg. Cash / Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={7} percentChange={calcPct(metrics?.avgCashPerApplication || 0, previousMetrics?.avgCashPerApplication)} />
        <StatCard label="Avg. Cash / Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={8} percentChange={calcPct(metrics?.avgCashPerOptin || 0, previousMetrics?.avgCashPerOptin)} />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Close Rate Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">Close Rate Trend</h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={closeRateTrend}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <defs>
                  <linearGradient id="closeRateGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={branding.colors.primary} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={branding.colors.primary} stopOpacity={0.05} />
                  </linearGradient>
                  <filter id="closeRateShadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor={branding.colors.primary} floodOpacity="0.5" />
                  </filter>
                </defs>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length && label) {
                      return (
                        <div className="bg-background/95 backdrop-blur-md border border-border shadow-xl rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">
                            {new Date(String(label)).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                          <p className="text-sm font-semibold text-foreground">
                            {formatPercent(payload[0].value as number)}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={branding.colors.primary}
                  strokeWidth={2}
                  fill="url(#closeRateGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: branding.colors.primary,
                    stroke: "white",
                    strokeWidth: 2,
                    filter: "url(#closeRateShadow)",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Deal Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">Deal Status Breakdown</h3>
          <div className="h-[350px]">
            <ResponsivePie
              data={toNivoPieData(dealStatusData, PIE_COLORS)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>
      </div>

      {/* Closer Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border overflow-hidden"
      >
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Closer Performance</h3>
              <p className="text-sm text-muted-foreground">Individual closer metrics and rankings</p>
            </div>
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Filter by name..."
                value={tableNameFilter}
                onChange={(e) => setTableNameFilter(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full sm:w-[200px]"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 font-medium text-muted-foreground">Closer Name</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Total Cash Collected</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Avg. Cash / Call</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCloserPerformance.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-muted-foreground">
                    {tableNameFilter ? "No closers match your filter" : "No closer data available"}
                  </td>
                </tr>
              ) : (
                paginatedCloserPerformance.map((closer, i) => (
                  <tr
                    key={closer.name}
                    className={`border-b border-border transition-colors hover:bg-muted/30 ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                  >
                    <td className="p-4 font-medium">{closer.name}</td>
                    <td className="p-4 text-right font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(closer.totalCash)}
                    </td>
                    <td className="p-4 text-right text-muted-foreground">
                      {formatCurrency(closer.avgCashPerCall)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredCloserPerformance.length > tablePageSize && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {((tablePage - 1) * tablePageSize) + 1} to {Math.min(tablePage * tablePageSize, filteredCloserPerformance.length)} of {filteredCloserPerformance.length} closers
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTablePage(1)}
                disabled={tablePage === 1}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                disabled={tablePage === 1}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="px-3 py-1 text-sm">
                Page {tablePage} of {totalTablePages}
              </span>
              <button
                onClick={() => setTablePage((p) => Math.min(totalTablePages, p + 1))}
                disabled={tablePage === totalTablePages}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => setTablePage(totalTablePages)}
                disabled={tablePage === totalTablePages}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
